import React from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import ErrorBoundary from '../../../../components/ErrorBoundary'
import { markdownComponents } from './markdown/MarkdownComponents'
import classNames from 'classnames'

interface MarkdownRendererProps {
  content: string
  isError?: boolean
}

/**
 * Component for rendering markdown content with custom styling.
 * Includes error handling and fallback rendering for markdown parsing failures.
 */
export function MarkdownRenderer({ content, isError = false }: MarkdownRendererProps) {
  return (
    <div
      className={classNames(
        'select-text text-sm',
        isError
          ? 'text-red-600 dark:text-red-400'
          : 'text-gray-700 dark:text-gray-300'
      )}
    >
      {/* Wrap ReactMarkdown in error boundary to prevent crashes */}
      <ErrorBoundary>
        {(() => {
          try {
            return (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={markdownComponents}
              >
                {content}
              </ReactMarkdown>
            )
          } catch (error) {
            console.warn('Markdown rendering failed:', error)
            return <MarkdownFallback content={content} />
          }
        })()}
      </ErrorBoundary>
    </div>
  )
}

/**
 * Fallback component displayed when markdown rendering fails.
 * Shows the raw text content in a styled error container.
 */
function MarkdownFallback({ content }: { content: string }) {
  return (
    <div className='rounded border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-950/30'>
      <p className='text-sm text-red-700 dark:text-red-400'>
        Failed to render markdown content. Here&apos;s the raw text:
      </p>
      <pre className='mt-2 whitespace-pre-wrap text-xs text-red-600 dark:text-red-300'>
        {content}
      </pre>
    </div>
  )
}